import { useMutation, useQueryClient } from "@tanstack/react-query";
import { manualMimshakDataService } from "../services/manualMimshakDataService";
import { ManualMimshakData } from "../interfaces/DTO/manualMimshakData";
import { createMimshakHooks } from "./createMimshakHooks";

// ✅ יצירת כל ה-hooks עבור Manual Mimshak
const hooks = createMimshakHooks<ManualMimshakData>({
  service: manualMimshakDataService,
  queryKeys: {
    byPeriod: (userId: number, period: string) => ["manualMimshakDataByPeriod", userId, period],
    latestRecords: (userId: number) => ["manualMimshakDataLatestRecords", userId],
    latestRecordsBatch: (userIds: string) => ["manualMimshakDataLatestRecords", "byContacts", userIds],
    byPeriodBatch: (userIds: string, period: string) => ["manualMimshakData", "byPeriodBatch", userIds, period],
  },
});

// ✅ ייצוא כל ה-hooks
export const useManualMimshaksByContactAndPeriod = hooks.useMimshaksByContactAndPeriod;
export const useManualMimshaksByContactsAndPeriod = hooks.useMimshaksByContactsAndPeriod;
export const useLatestManualMimshakByContact = hooks.useLatestMimshakByContact;
export const useLatestManualMimshaksByContacts = hooks.useLatestMimshaksByContacts;


// ✅ פונקציה מרכזית לעדכון cache עבור contact
const updateManualMimshakCache = async (
  queryClient: any,
  contactId: number,
  reportedPeriod?: string
) => {
  // שליפת נתונים עדכניים מהשרת
  const updatedData = await manualMimshakDataService.getLatestRecordsByUser(contactId);

  console.log(`🔄 Updating cache for contact ${contactId}, data:`, updatedData);

  // עדכון ה-cache של ה-contact הבודד
  queryClient.setQueryData(
    ["manualMimshakDataLatestRecords", contactId],
    updatedData
  );

  // עדכון period-specific query אם קיים
  if (reportedPeriod) {
    queryClient.setQueryData(
      ["manualMimshakDataByPeriod", contactId, reportedPeriod],
      updatedData
    );
  }

  console.log(`✅ Cache updated for contact ${contactId}`);
};

// ✅ Hook ליצירה - עם cache update אוטומטי
export const useCreateManualMimshakData = () => {
  const queryClient = useQueryClient();

  return useMutation<ManualMimshakData, Error, ManualMimshakData>({
    mutationFn: (data) => manualMimshakDataService.create(data),
    onSuccess: async (data) => {
      await updateManualMimshakCache(queryClient, data.contact_id, data.reported_period);

      // ✅ Invalidate batch queries כדי לעדכן רשימות של מספר contacts
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords", "byContacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakData", "byPeriodBatch"],
      });
    },
  });
};

// ✅ Hook לעדכון מלא - עם cache update אוטומטי
export const useUpdateManualMimshakData = () => {
  const queryClient = useQueryClient();

  return useMutation<ManualMimshakData, Error, { id: number; data: Partial<ManualMimshakData> }>({
    mutationFn: ({ id, data }) => manualMimshakDataService.update(id, data),
    onSuccess: async (result) => {
      await updateManualMimshakCache(queryClient, result.contact_id, result.reported_period);

      // ✅ Invalidate batch queries כדי לעדכן רשימות של מספר contacts
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords", "byContacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakData", "byPeriodBatch"],
      });
    },
  });
};

// ✅ Hook לעדכון חלקי (PATCH) - עם cache update אוטומטי
export const usePartialUpdateManualMimshakData = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: number; data: Partial<ManualMimshakData>; contact_id: number; reported_period?: string }>({
    mutationFn: async ({ id, data }) => {
      await manualMimshakDataService.partialUpdate(id, data);
    },
    onSuccess: async (_, variables) => {
      await updateManualMimshakCache(queryClient, variables.contact_id, variables.reported_period);

      // ✅ Invalidate batch queries כדי לעדכן רשימות של מספר contacts
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords", "byContacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakData", "byPeriodBatch"],
      });
    },
  });
};

// ✅ Hook למחיקה - עם cache invalidation
export const useDeleteManualMimshakData = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => manualMimshakDataService.delete(id),
    onSuccess: () => {
      // ✅ במחיקה - invalidate את כל ה-queries כי לא יודעים איזה contact נמחק
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataByPeriod"],
      });
    },
  });
};

// ✅ Hook למחיקה לפי מזהים - מוחק את כל ההיסטוריה
export const useDeleteManualMimshakByIdentifiers = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { message: string; deleted_count: number },
    Error,
    {
      contact_id: number;
      handler_code: string;
      product_type: number;
      policy_number: string;
    }
  >({
    mutationFn: ({ contact_id, handler_code, product_type, policy_number }) =>
      manualMimshakDataService.deleteByIdentifiers(
        contact_id,
        handler_code,
        product_type,
        policy_number
      ),
    onSuccess: async (_, variables) => {
      await updateManualMimshakCache(queryClient, variables.contact_id);

      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataByPeriod", variables.contact_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords", "byContacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakData", "byPeriodBatch"],
      });
    },
  });
};

// ✅ Hook לעדכון מזהים - מעדכן את כל ההיסטוריה
export const useUpdateManualMimshakIdentifiers = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { message: string; updated_count: number },
    Error,
    {
      contact_id: number;
      old_handler_code: string;
      old_product_type: number;
      old_policy_number: string;
      new_handler_code: string;
      new_product_type: number;
      new_policy_number: string;
    }
  >({
    mutationFn: ({
      contact_id,
      old_handler_code,
      old_product_type,
      old_policy_number,
      new_handler_code,
      new_product_type,
      new_policy_number,
    }) =>
      manualMimshakDataService.updateIdentifiers(
        contact_id,
        old_handler_code,
        old_product_type,
        old_policy_number,
        new_handler_code,
        new_product_type,
        new_policy_number
      ),
    onSuccess: async (_, variables) => {
      // עדכון cache לאחר שינוי מזהים
      await updateManualMimshakCache(queryClient, variables.contact_id);

      // ✅ Invalidate batch queries כדי לעדכן רשימות של מספר contacts
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakDataLatestRecords", "byContacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["manualMimshakData", "byPeriodBatch"],
      });
    },
  });
};
